<template>
    <div>
        <div class="main-container text-left">
            <KidHeader :profileId="profileId" :serialNumber="serialNumber" />

            <!-- Back nav -->
            <div class="req-back">
                <a class="req-back__link" href="javascript:void(0)"
                    @click="$router.push({ path: '/kids-device', query: { profileId: profileId, serialNumber: serialNumber } })">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                        <path d="M19 12H5M5 12l7 7M5 12l7-7" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                    Home
                </a>
            </div>

            <!-- Page header -->
            <div class="sc-page-header">
                <div>
                    <h1 class="sc-page-header__title">Schedules</h1>
                    <p class="sc-page-header__sub">Manage device usage time restrictions</p>
                </div>
                <button class="sc-create-btn" @click="navigateToCreate()">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                        <path d="M12 5v14M5 12h14" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                    </svg>
                    New
                </button>
            </div>

            <!-- Loader -->
            <div class="sc-loader" v-if="isLoading">
                <i class="fa fa-spinner fa-spin"></i>
                <span>Loading schedules...</span>
            </div>

            <template v-else>
                <!-- Empty state -->
                <div class="sc-empty" v-if="schedules.length === 0">
                    <div class="sc-empty__icon">
                        <svg width="36" height="36" viewBox="0 0 24 24" fill="none">
                            <rect x="3" y="4" width="18" height="18" rx="2" stroke="#d1d5db" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                            <path d="M16 2v4M8 2v4M3 10h18" stroke="#d1d5db" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                    </div>
                    <span class="sc-empty__title">No schedules yet</span>
                    <span class="sc-empty__sub">Create a schedule to manage device usage</span>
                    <button class="sc-create-btn sc-create-btn--full" @click="navigateToCreate()">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                            <path d="M12 5v14M5 12h14" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                        </svg>
                        Create Your First Schedule
                    </button>
                </div>

                <!-- Schedule list -->
                <div class="sc-list" v-else>
                    <div class="sc-card" v-for="schedule in schedules" :key="schedule._id"
                        :class="{ 'sc-card--inactive': !schedule.isActive }">

                        <!-- Card header -->
                        <div class="sc-card__header">
                            <div class="sc-card__icon" :class="getScheduleIconClass(schedule.type)">
                                <i :class="getScheduleIcon(schedule.type)"></i>
                            </div>
                            <div class="sc-card__info">
                                <span class="sc-card__name">{{ schedule.label || 'Unnamed Schedule' }}</span>
                                <div class="sc-card__meta">
                                    <span class="sc-badge" :class="getScheduleTypeBadge(schedule.type)">
                                        {{ getScheduleTypeText(schedule.type) }}
                                    </span>
                                    <span class="sc-status" :class="schedule.isActive ? 'sc-status--on' : 'sc-status--off'">
                                        <span class="sc-status__dot"></span>
                                        {{ schedule.isActive ? 'Active' : 'Inactive' }}
                                    </span>
                                </div>
                            </div>
                            <label class="kid-action-switch" @click.stop>
                                <input type="checkbox"
                                    :checked="schedule.isActive"
                                    @change="toggleScheduleStatus(schedule)"
                                    :disabled="schedule.isToggling"
                                />
                                <span class="kid-action-switch__track"></span>
                            </label>
                        </div>

                        <!-- Days strip -->
                        <div class="sc-days-strip">
                            <span
                                v-for="day in schedule.days" :key="day"
                                class="sc-day">
                                {{ getDayShort(day) }}
                            </span>
                        </div>

                        <!-- Restrictions -->
                        <div class="sc-restrictions">
                            <div class="sc-section-label">
                                <svg width="11" height="11" viewBox="0 0 24 24" fill="none">
                                    <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="1.8"/>
                                    <path d="M12 6v6l4 2" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                                </svg>
                                Restrictions ({{ schedule.restrictions.length }})
                            </div>

                            <div class="sc-restriction-list">
                                <div class="sc-restriction"
                                    v-for="(restriction, index) in schedule.restrictions" :key="index"
                                    :class="getRestrictionClass(restriction.mode)">
                                    <div class="sc-restriction__icon" :class="getRestrictionIconClass(restriction.mode)">
                                        <i :class="getRestrictionIcon(restriction.mode)"></i>
                                    </div>
                                    <div class="sc-restriction__body">
                                        <span class="sc-restriction__time">
                                            <template v-if="restriction.mode === 'sleep' || restriction.mode === 'apps'">
                                                {{ formatTime(restriction.startTime) }} – {{ formatTime(restriction.endTime) }}
                                            </template>
                                            <template v-else-if="restriction.mode === 'limit'">
                                                {{ restriction.dailyLimitMinutes }} min daily limit
                                            </template>
                                        </span>
                                        <div class="sc-overnight" v-if="isOvernightSchedule(restriction) && (restriction.mode === 'sleep' || restriction.mode === 'apps')">
                                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none">
                                                <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                                            </svg>
                                            Overnight - Continue until next morning
                                        </div>
                                        <span class="sc-restriction__sub" v-if="restriction.mode === 'apps' && restriction.restrictedApps && restriction.restrictedApps.length > 0">
                                            {{ restriction.restrictedApps.length }} app{{ restriction.restrictedApps.length > 1 ? 's' : '' }} restricted
                                        </span>
                                    </div>
                                    <span class="sc-mode-badge" :class="getModeBadgeClass(restriction.mode)">
                                        {{ getModeText(restriction.mode) }}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <!-- Footer actions -->
                        <div class="sc-card__footer">
                            <button class="sc-action-btn sc-action-btn--edit"
                                @click="editSchedule(schedule)"
                                :disabled="schedule.isDeleting">
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                                </svg>
                                Edit
                            </button>
                            <button class="sc-action-btn sc-action-btn--delete"
                                @click="confirmDelete(schedule)"
                                :disabled="schedule.isDeleting">
                                <i v-if="schedule.isDeleting" class="fa fa-spinner fa-spin"></i>
                                <svg v-else width="13" height="13" viewBox="0 0 24 24" fill="none">
                                    <polyline points="3 6 5 6 21 6" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                                    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                                    <path d="M10 11v6M14 11v6M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                                </svg>
                                {{ schedule.isDeleting ? 'Deleting...' : 'Delete' }}
                            </button>
                        </div>
                    </div>
                </div>
            </template>

            <!-- Delete confirm modal -->
            <div class="sc-modal-overlay" v-if="deleteModal.show" @click.self="deleteModal.show = false">
                <div class="sc-modal">
                    <div class="sc-modal__title">Delete Schedule?</div>
                    <div class="sc-modal__body">
                        Are you sure you want to delete <strong>{{ deleteModal.schedule?.label }}</strong>? This action cannot be undone.
                    </div>
                    <div class="sc-modal__footer">
                        <button class="sc-modal__cancel" @click="deleteModal.show = false" :disabled="deleteModal.isDeleting">Cancel</button>
                        <button class="sc-modal__confirm" @click="deleteSchedule()" :disabled="deleteModal.isDeleting">
                            <span v-if="deleteModal.isDeleting"><i class="fa fa-spinner fa-spin"></i></span>
                            <span v-else>Delete</span>
                        </button>
                    </div>
                </div>
            </div>

            <div class="sc-footer-note" v-if="!isLoading && schedules.length > 0">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="1.8"/>
                    <path d="M12 6v6l4 2" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                </svg>
                {{ schedules.length }} schedule{{ schedules.length !== 1 ? 's' : '' }}, {{ activeSchedulesCount }} active
            </div>

            <KidFooter :profileId="profileId" :serialNumber="serialNumber" />
        </div>
    </div>
</template>

<script>
import KidHeader from '@/components/KidHeader.vue'
import KidFooter from '@/components/KidFooter.vue'
import ScheduleService from "../../services/ScheduleService.js"
import ProfileService from "../../services/ProfileService"
import KidsService from "../../services/KidsService"

export default {
    name: 'Schedule',
    components: { KidHeader, KidFooter },
    data() {
        return {
            profileId: this.$route.query.profileId,
            serialNumber: this.$route.query.serialNumber,
            schedules: [],
            isLoading: false,
            deleteModal: { show: false, schedule: null, isDeleting: false }
        }
    },
    computed: {
        activeSchedulesCount() { return this.schedules.filter(s => s.isActive).length; },
        totalRestrictionsCount() { return this.schedules.reduce((t, s) => t + (s.restrictions ? s.restrictions.length : 0), 0); }
    },
    methods: {
        async loadSchedules() {
            this.isLoading = true;
            try {
                const response = await ScheduleService.getSchedules(this.profileId, this.serialNumber);
                if (response.data.success) {
                    this.schedules = (response.data.data || []).map(s => ({ ...s, isToggling: false, isDeleting: false }));
                } else {
                    this.$toast.error(response.data.message || 'Failed to load schedules');
                }
            } catch (error) {
                this.$toast.error(error.response?.data?.message || 'Failed to load schedules');
            } finally {
                this.isLoading = false;
            }
        },
        navigateToCreate() {
            this.$router.push({ path: '/create-schedule', query: { profileId: this.profileId, serialNumber: this.serialNumber } });
        },
        editSchedule(schedule) {
            this.$router.push({ path: '/create-schedule', query: { profileId: this.profileId, serialNumber: this.serialNumber, scheduleId: schedule._id, edit: 'true' } });
        },
        confirmDelete(schedule) { this.deleteModal.schedule = schedule; this.deleteModal.show = true; },
        async deleteSchedule() {
            if (!this.deleteModal.schedule) return;
            this.deleteModal.isDeleting = true;
            const schedule = this.deleteModal.schedule;
            schedule.isDeleting = true;
            try {
                const response = await ScheduleService.deleteSchedule(schedule._id, { profileId: this.profileId, serialNumber: this.serialNumber });
                if (response.data.success) {
                    //this.$toast.success('Schedule deleted');
                    /*await KidsService.toggleSleepMode({ profileId: this.profileId, sleepMode: false, serialNumber: this.serialNumber });
                    await ProfileService.applyPolicy({ profile_id: this.profileId, serialNumber: this.serialNumber });*/
                    const index = this.schedules.findIndex(s => s._id === schedule._id);
                    if (index > -1) this.schedules.splice(index, 1);
                    this.deleteModal.show = false;
                    this.deleteModal.schedule = null;
                    this.loadSchedules()
                } else {
                    this.deleteModal.show = false;
                    this.deleteModal.schedule = null;
                    this.$toast.error(response.data.message || 'Failed to delete');
                }
            } catch (error) {
                this.deleteModal.show = false;
                this.deleteModal.schedule = null;
                this.$toast.error(error.response?.data?.message || 'Failed to delete');
            } finally {
                this.deleteModal.isDeleting = false;
                schedule.isDeleting = false;
            }
        },
        isOvernightSchedule(restriction) {
            if (!restriction.startTime || !restriction.endTime) return false;
            const [sh, sm] = restriction.startTime.split(':').map(Number);
            const [eh, em] = restriction.endTime.split(':').map(Number);
            return (eh * 60 + em) < (sh * 60 + sm);
        },
        async toggleScheduleStatus(schedule) {
            if (schedule.isToggling) return;
            const newStatus = !schedule.isActive;
            const oldStatus = schedule.isActive;
            schedule.isActive = newStatus;
            schedule.isToggling = true;
            try {
                const response = await ScheduleService.toggleScheduleStatus(schedule._id, { profileId: this.profileId, serialNumber: this.serialNumber, isActive: newStatus });
                if (response.data.success) {
                    this.$toast.success(`Schedule ${newStatus ? 'activated' : 'deactivated'}`);
                    /*if (!newStatus) {
                        await KidsService.toggleSleepMode({ profileId: this.profileId, sleepMode: false, serialNumber: this.serialNumber });
                        await ProfileService.applyPolicy({ profile_id: this.profileId, serialNumber: this.serialNumber });
                    }*/
                } else {
                    schedule.isActive = oldStatus;
                    this.$toast.error(response.data.message || 'Failed to update status');
                }
            } catch (error) {
                schedule.isActive = oldStatus;
                if (error.response && error.response.status === 409) {
                    let message = 'Cannot activate schedule due to conflicts:\n\n';
                    if (error.response.data.conflicts) error.response.data.conflicts.forEach(c => { message += `• ${c.message}\n`; });
                    alert(message);
                    this.$toast.error('Schedule has conflicts');
                } else {
                    this.$toast.error(error.response?.data?.message || 'Failed to update status');
                }
            } finally {
                schedule.isToggling = false;
            }
        },
        getScheduleTypeBadge(type) { return { custom: 'sc-badge--blue', weekday: 'sc-badge--purple', weekend: 'sc-badge--green', everyday: 'sc-badge--orange' }[type] || 'sc-badge--blue'; },
        getScheduleTypeText(type) { return { custom: 'Custom', weekday: 'Weekdays', weekend: 'Weekend', everyday: 'Every Day' }[type] || 'Custom'; },
        getScheduleIcon(type) { return { custom: 'fas fa-sliders-h', weekday: 'fas fa-briefcase', weekend: 'fas fa-calendar-week', everyday: 'fas fa-redo' }[type] || 'fas fa-calendar'; },
        getScheduleIconClass(type) { return { custom: 'sc-card__icon--blue', weekday: 'sc-card__icon--purple', weekend: 'sc-card__icon--green', everyday: 'sc-card__icon--orange' }[type] || 'sc-card__icon--blue'; },
        getDayShort(day) { return { monday: 'Mon', tuesday: 'Tue', wednesday: 'Wed', thursday: 'Thu', friday: 'Fri', saturday: 'Sat', sunday: 'Sun' }[day] || day; },
        isWeekend(day) { return day === 'saturday' || day === 'sunday'; },
        getRestrictionClass(mode) { return { sleep: 'sc-restriction--sleep', limit: 'sc-restriction--limit', apps: 'sc-restriction--apps' }[mode] || ''; },
        getRestrictionIcon(mode) { return { sleep: 'fas fa-ban', limit: 'fas fa-hourglass-half', apps: 'fas fa-mobile-alt' }[mode] || 'fas fa-clock'; },
        getRestrictionIconClass(mode) { return { sleep: 'sc-restriction__icon--red', limit: 'sc-restriction__icon--orange', apps: 'sc-restriction__icon--purple' }[mode] || ''; },
        getModeBadgeClass(mode) { return { sleep: 'sc-mode-badge--sleep', limit: 'sc-mode-badge--limit', apps: 'sc-mode-badge--apps' }[mode] || ''; },
        getModeText(mode) { return { sleep: 'Sleep', limit: 'Limit', apps: 'Apps' }[mode] || mode; },
        formatTime(time) {
            if (!time) return '';
            const [hours, minutes] = time.split(':');
            const h = parseInt(hours);
            return `${h % 12 || 12}:${minutes} ${h >= 12 ? 'PM' : 'AM'}`;
        }
    },
    mounted() { this.loadSchedules(); }
}
</script>

<style scoped>
/* ── Back nav ── */
.req-back { padding: 12px 14px 8px; width: 100%; text-align: left; }
.req-back__link {
    display: inline-flex; align-items: center; gap: 6px;
    font-size: 13px; font-weight: 600; color: #374151;
    text-decoration: none; -webkit-tap-highlight-color: transparent;
}

/* ── Page header ── */
.sc-page-header {
    display: flex; align-items: center; justify-content: space-between;
    padding: 4px 14px 14px;
    width: 100%;
}
.sc-page-header__title { font-size: 20px; font-weight: 700; color: #111; margin: 0 0 2px; }
.sc-page-header__sub { font-size: 12px; color: #6b7280; margin: 0; }

/* ── Create button ── */
.sc-create-btn {
    display: inline-flex; align-items: center; gap: 5px;
    height: 36px; padding: 0 14px;
    background: #2563eb; color: #fff;
    border: none; border-radius: 10px;
    font-size: 13px; font-weight: 600;
    cursor: pointer; flex-shrink: 0;
    -webkit-tap-highlight-color: transparent;
}
.sc-create-btn:active { opacity: 0.85; }
.sc-create-btn--full { width: 100%; justify-content: center; height: 44px; border-radius: 12px; margin-top: 16px; }

/* ── Loader ── */
.sc-loader {
    display: flex; flex-direction: column; align-items: center;
    gap: 10px; padding: 48px 14px; color: #6b7280; font-size: 13px;
}

/* ── Empty ── */
.sc-empty {
    display: flex; flex-direction: column; align-items: center;
    gap: 6px; padding: 48px 14px; text-align: center;
}
.sc-empty__icon {
    width: 72px; height: 72px; border-radius: 50%;
    background: #f3f4f6;
    display: flex; align-items: center; justify-content: center; margin-bottom: 8px;
}
.sc-empty__title { font-size: 16px; font-weight: 700; color: #111; }
.sc-empty__sub { font-size: 13px; color: #6b7280; }

/* ── List ── */
.sc-list { display: flex; flex-direction: column; gap: 10px; padding: 0 14px; width: 100%; }

/* ── Card ── */
.sc-card {
    background: #fff; border-radius: 14px;
    border: 0.5px solid #e5e7eb;
    overflow: hidden; transition: opacity 0.15s;
    width: 100%;
}
.sc-card--inactive { opacity: 0.65; }
.sc-card--inactive:hover { opacity: 1; }

/* ── Card header ── */
.sc-card__header {
    display: flex; align-items: center; gap: 12px;
    padding: 14px 14px 12px;
    border-bottom: 0.5px solid #f3f4f6;
}
.sc-card__icon {
    width: 40px; height: 40px; border-radius: 11px;
    display: flex; align-items: center; justify-content: center;
    font-size: 16px; color: #fff; flex-shrink: 0;
}
.sc-card__icon--blue { background: #2563eb; }
.sc-card__icon--purple { background: #7c3aed; }
.sc-card__icon--green { background: #16a34a; }
.sc-card__icon--orange { background: #ea580c; }

.sc-card__info { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 4px; }
.sc-card__name { font-size: 14px; font-weight: 700; color: #111; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.sc-card__meta { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }

/* ── Badge ── */
.sc-badge {
    font-size: 10px; font-weight: 600;
    padding: 2px 8px; border-radius: 20px; text-transform: capitalize;
}
.sc-badge--blue { background: #dbeafe; color: #1e40af; }
.sc-badge--purple { background: #ede9fe; color: #6b21a8; }
.sc-badge--green { background: #dcfce7; color: #15803d; }
.sc-badge--orange { background: #ffedd5; color: #9a3412; }

/* ── Status ── */
.sc-status { display: inline-flex; align-items: center; gap: 4px; font-size: 11px; font-weight: 600; }
.sc-status__dot { width: 6px; height: 6px; border-radius: 50%; background: currentColor; }
.sc-status--on { color: #16a34a; }
.sc-status--off { color: #9ca3af; }

/* ── Switch ── */
.kid-action-switch { position: relative; display: inline-flex; width: 38px; height: 20px; cursor: pointer; margin: 0; flex-shrink: 0; }
.kid-action-switch input { opacity: 0; width: 0; height: 0; position: absolute; }
.kid-action-switch__track { position: absolute; inset: 0; background: #d1d5db; border-radius: 999px; transition: background 0.2s; }
.kid-action-switch__track::after { content: ''; position: absolute; top: 3px; left: 3px; width: 14px; height: 14px; background: white; border-radius: 50%; transition: transform 0.2s; }
.kid-action-switch input:checked + .kid-action-switch__track { background: #2563eb; }
.kid-action-switch input:checked + .kid-action-switch__track::after { transform: translateX(18px); }

/* ── Days strip ── */
.sc-days-strip {
    display: flex; gap: 6px; padding: 10px 14px;
    flex-wrap: wrap; border-bottom: 0.5px solid #f3f4f6;
}
.sc-day {
    display: inline-flex; align-items: center; justify-content: center;
    height: 26px; padding: 0 10px;
    background: #f3f4f6; border-radius: 20px;
    font-size: 11px; font-weight: 600; color: #374151;
}
.sc-day--weekend { background: #fef9c3; color: #854d0e; }

/* ── Restrictions ── */
.sc-restrictions { padding: 12px 14px 4px; }
.sc-section-label {
    display: inline-flex; align-items: center; gap: 5px;
    font-size: 10px; font-weight: 700; color: #9ca3af;
    text-transform: uppercase; letter-spacing: 0.5px;
    margin-bottom: 8px;
}
.sc-restriction-list { display: flex; flex-direction: column; gap: 8px; margin-bottom: 10px; }

.sc-restriction {
    display: flex; align-items: center; gap: 10px;
    padding: 10px 12px; border-radius: 10px;
    border-left: 3px solid #e5e7eb;
}
.sc-restriction--sleep { background: #fff1f2; border-left-color: #dc2626; }
.sc-restriction--limit { background: #fffbeb; border-left-color: #f59e0b; }
.sc-restriction--apps { background: #f5f3ff; border-left-color: #7c3aed; }

.sc-restriction__icon {
    width: 30px; height: 30px; border-radius: 8px;
    display: flex; align-items: center; justify-content: center;
    font-size: 12px; flex-shrink: 0;
}
.sc-restriction__icon--red { background: #fecaca; color: #dc2626; }
.sc-restriction__icon--orange { background: #fde68a; color: #b45309; }
.sc-restriction__icon--purple { background: #ddd6fe; color: #7c3aed; }

.sc-restriction__body { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 2px; }
.sc-restriction__time { font-size: 13px; font-weight: 600; color: #111; }
.sc-restriction__sub { font-size: 11px; color: #6b7280; }

.sc-overnight {
    display: inline-flex; align-items: center; gap: 4px;
    font-size: 10px; font-weight: 600; color: #b45309;
    background: #fef9c3; padding: 2px 7px;
    border-radius: 20px; width: fit-content;
}

.sc-mode-badge {
    font-size: 10px; font-weight: 700;
    padding: 3px 8px; border-radius: 20px;
    white-space: nowrap; flex-shrink: 0;
}
.sc-mode-badge--sleep { background: #fee2e2; color: #dc2626; }
.sc-mode-badge--limit { background: #fef9c3; color: #b45309; }
.sc-mode-badge--apps { background: #ede9fe; color: #7c3aed; }

/* ── Footer actions ── */
.sc-card__footer {
    display: flex; gap: 8px;
    padding: 10px 14px; background: #f9fafb;
}
.sc-action-btn {
    display: inline-flex; align-items: center; gap: 5px;
    height: 34px; padding: 0 12px; border-radius: 8px;
    font-size: 12px; font-weight: 600; cursor: pointer;
    flex: 1; justify-content: center;
    border: 0.5px solid #e5e7eb;
    white-space: nowrap;
    -webkit-tap-highlight-color: transparent; transition: opacity 0.12s;
}
.sc-action-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.sc-action-btn:active:not(:disabled) { opacity: 0.75; }
.sc-action-btn--edit { background: #eff6ff; color: #2563eb; border-color: #bfdbfe; }
.sc-action-btn--delete { background: #fff5f5; color: #dc2626; border-color: #fecaca; }

/* ── Delete modal ── */
.sc-modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0,0,0,0.45);
    z-index: 300;
    display: flex;
    align-items: flex-end;
    justify-content: center;
}

.sc-modal {
    background: #fff;
    border-radius: 20px 20px 0 0;
    width: 100%;
    max-width: 575px;
    padding: 20px 16px 32px;
    animation: slideUp 0.25s ease;
}
@media (max-width: 575px) {
    .sc-modal {
        padding-bottom: calc(60px + env(safe-area-inset-bottom, 16px));
    }
}
@keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
.sc-modal__title { font-size: 17px; font-weight: 700; color: #111; margin-bottom: 8px; }
.sc-modal__body { font-size: 13px; color: #6b7280; line-height: 1.5; margin-bottom: 20px; }
.sc-modal__footer { display: flex; gap: 10px; }
.sc-modal__cancel {
    flex: 1; height: 44px; border-radius: 12px;
    border: 0.5px solid #e5e7eb; background: #f3f4f6;
    font-size: 14px; font-weight: 600; color: #374151; cursor: pointer;
}
.sc-modal__confirm {
    flex: 1; height: 44px; border-radius: 12px; border: none;
    background: #dc2626; color: #fff;
    font-size: 14px; font-weight: 600; cursor: pointer;
}
.sc-modal__confirm:disabled, .sc-modal__cancel:disabled { opacity: 0.6; cursor: not-allowed; }

/* ── Footer note ── */
.sc-footer-note {
    display: flex; align-items: center; justify-content: center;
    gap: 5px; font-size: 12px; color: #9ca3af;
    padding: 16px 0 24px;
}

button:focus { outline: none; }
</style>